import { useState } from "react";
import { Plus, Minus, HelpCircle, Phone, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";

/**
 * ⚠️ 10Web AI WARNING — DO NOT REPLACE <Icon /> JSX with <img> tags.
 * All lucide icons here are wrapped with data-icon-container="lucide-do-not-replace".
 * Keep them as React components.
 */

const NAVY = "#081438";
const NAVY_DEEP = "#050D24";
const NAVY_LIGHT = "#243752";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
const FAQS = [{
  q: "How much more does an insulated door cost vs a single-skin?",
  a: "Insulated doors typically run 30–60% above an equivalent single-skin. Final price depends on size, brand, ColorBond finish, motor compatibility, and any wall preparation. We quote flat-price on the phone after a quick measure visit — no online estimator guesswork. Most Sydney homes see payback in comfort and resale within 3–4 years."
}, {
  q: "Will my existing garage motor work with an insulated door?",
  a: "Often yes — but not always. Insulated doors weigh roughly 50% more than single-skin. Modern motors (made in the last 8–10 years) with 800N+ pull usually handle the extra mass fine; older or lighter-spec motors may struggle. We do a free motor-spec check during the measure visit and tell you honestly — no automatic motor-swap upsell."
}, {
  q: "Can I retro-fit insulation to my existing single-skin door?",
  a: "Technically yes — foam-board kits exist that adhere to the inside face. But the result is cosmetic insulation only: no edge seal, no thermal break, no acoustic benefit, and they often unbalance the springs. For real thermal &amp; acoustic performance, a proper insulated panel door is the only path. We don't install foam-board kits."
}, {
  q: "How long does the installation take?",
  a: "Most sectional installs are a single-day job — about 4–6 hours including stripping the old door, installing the new one, hanging tracks, spring tension, motor commissioning, and a full open/close test. Roller door installs are usually a half-day. You'll be quoted on a 1-day arrival window; we never split installs across multiple days."
}, {
  q: "Are insulated doors worth it if I don't have a room above the garage?",
  a: "Yes — especially in Sydney. Even without a room above, you get a cooler garage in summer (workshop, gym, storage), a quieter door (less morning &amp; late-night neighbour drama), a stiffer panel that resists dings, and a finish that holds colour through Sydney UV. The resale uplift alone often justifies the upgrade, room-above or not."
}, {
  q: "What's the difference between R-value, U-value, and STC?",
  a: "R-value measures thermal resistance — higher is better (R-2.0 is the typical insulated door rating). U-value is the inverse — heat transfer rate — so lower is better. STC (Sound Transmission Class) measures acoustic dampening — higher is better (STC 27+ is what insulated doors achieve, vs ~17 for single-skin). All three are listed on every door spec we install."
}, {
  q: "Will an insulated roller door be as effective as a sectional?",
  a: "Not quite — but for low-headroom garages where a sectional won't fit, an insulated roller (R-1.2 vs R-2.0) still delivers a meaningful thermal &amp; acoustic improvement over a non-insulated equivalent. It's the right choice for granny flats, inner-west terraces, and apartment carparks. Where space allows, sectional is the better thermal investment."
}, {
  q: "What warranty comes with the door and the install?",
  a: "Two layers. The manufacturer warranty covers panels, springs, and finish — typically 5–10 years depending on brand, with marine-grade options on coastal installs. On top of that, we add our own 1-Year Labour Warranty on the installation work itself. If anything fails inside that window for any reason — workmanship or component — we come back and sort it, no charge."
}];
export default function InsulatedFAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const schemaJson = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map(f => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.a.replace(/&amp;/g, "&")
      }
    }))
  };
  return <section className="relative overflow-hidden" style={{
    backgroundColor: NAVY,
    fontFamily: FONT_BODY,
    paddingTop: "110px",
    paddingBottom: "110px"
  }}>
      {/* SEO: FAQ Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(schemaJson)
    }} />

      {/* Ambient radial */}
      <div aria-hidden className="absolute inset-0" style={{
      background: `radial-gradient(circle at 10% 0%, ${NAVY_LIGHT}55 0%, ${NAVY} 50%), radial-gradient(circle at 90% 100%, ${GOLD}10 0%, transparent 40%)`
    }} />

      {/* Grain */}
      <div aria-hidden className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")"
    }} />

      {/* Top gold scanline */}
      <div aria-hidden className="absolute top-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent, ${GOLD}66 50%, transparent)`
    }} />

      {/* Watermark */}
      <div aria-hidden className="absolute pointer-events-none select-none" style={{
      right: "-30px",
      top: "40px",
      fontFamily: FONT_HEAD,
      fontSize: "240px",
      fontWeight: 800,
      lineHeight: 1,
      letterSpacing: "-0.05em",
      color: `${GOLD}12`
    }}>
        09
      </div>

      <div className="relative max-w-[1180px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-12 lg:gap-16 items-start">
          {/* LEFT — sticky header */}
          <div className="lg:sticky lg:top-28">
            <div className="mb-5 flex items-center gap-3">
              <div aria-hidden className="h-px w-12" style={{
              backgroundColor: GOLD
            }} />
              <span style={{
              fontFamily: FONT_HEAD,
              fontSize: "12px",
              fontWeight: 700,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: GOLD
            }}>
                09 · Insulated Doors FAQ
              </span>
            </div>

            <h2 style={{
            fontFamily: FONT_HEAD,
            fontWeight: 800,
            fontSize: "clamp(2.1rem, 4vw, 3.4rem)",
            lineHeight: 1.05,
            letterSpacing: "-0.025em",
            color: "#ffffff",
            marginBottom: "20px"
          }}>
              The questions
              <br />
              <span style={{
              color: GOLD
            }}>before you commit</span>.
            </h2>

            <p style={{
            fontFamily: FONT_BODY,
            fontSize: "16px",
            lineHeight: 1.7,
            color: "rgba(255,255,255,0.72)",
            maxWidth: "420px",
            marginBottom: "32px"
          }}>
              Eight things every homeowner asks before going insulated.
              Straight answers — no glossy brochure spin.
            </p>

            {/* Help card */}
            <div className="rounded-2xl p-6 relative overflow-hidden" style={{
            background: `linear-gradient(135deg, ${NAVY_LIGHT}AA 0%, ${NAVY_DEEP} 100%)`,
            border: `1px solid ${GOLD}33`
          }}>
              <div className="flex items-start gap-4 mb-5">
                <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex items-center justify-center rounded-lg flex-shrink-0" style={{
                width: 44,
                height: 44,
                backgroundColor: `${GOLD}22`,
                border: `1px solid ${GOLD}44`
              }}>
                  <HelpCircle size={20} color={GOLD} strokeWidth={2.5} />
                </span>
                <div>
                  <h3 style={{
                  fontFamily: FONT_HEAD,
                  fontWeight: 800,
                  fontSize: "1.05rem",
                  color: "#ffffff",
                  letterSpacing: "-0.01em",
                  marginBottom: "4px"
                }}>
                    Want a closer look?
                  </h3>
                  <p style={{
                  fontFamily: FONT_BODY,
                  fontSize: "13px",
                  color: "rgba(255,255,255,0.7)",
                  lineHeight: 1.55
                }}>
                    Send a photo of your current door on WhatsApp — we'll
                    tell you straight which insulated model fits and roughly
                    what to expect.
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-2.5">
                <a href="tel:0485000933" className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full transition-all hover:scale-[1.02]" style={{
                backgroundColor: GOLD,
                color: NAVY,
                fontFamily: FONT_HEAD,
                fontWeight: 700,
                fontSize: "13px",
                boxShadow: `0 6px 18px ${GOLD}33`
              }}>
                  <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                    <Phone size={14} strokeWidth={2.5} />
                  </span>
                  Call 0485 000 933
                </a>

                <a href="https://wa.me/61485000933?text=Hi%2C%20I%27d%20like%20to%20send%20a%20photo%20of%20my%20garage%20for%20an%20insulated%20door%20quote." target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full transition-all hover:scale-[1.02]" style={{
                backgroundColor: "#25D366",
                color: "#081438",
                fontFamily: FONT_HEAD,
                fontWeight: 700,
                fontSize: "13px"
              }}>
                  WhatsApp a photo
                  <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                    <ArrowRight size={13} strokeWidth={2.5} />
                  </span>
                </a>
              </div>
            </div>
          </div>

          {/* RIGHT — accordion */}
          <div>
            <ul className="space-y-3">
              {FAQS.map((faq, i) => {
              const isOpen = openIndex === i;
              return <li key={faq.q} className="rounded-xl overflow-hidden transition-all" style={{
                backgroundColor: isOpen ? `${NAVY_LIGHT}88` : `${NAVY_LIGHT}33`,
                border: `1px solid ${isOpen ? `${GOLD}44` : "rgba(255,255,255,0.08)"}`
              }}>
                    <button type="button" onClick={() => setOpenIndex(isOpen ? null : i)} className="w-full text-left flex items-start justify-between gap-4 p-5 md:p-6 transition-colors" aria-expanded={isOpen}>
                      <div className="flex items-start gap-4 flex-1">
                        {/* Q number */}
                        <span className="flex-shrink-0" style={{
                      fontFamily: FONT_HEAD,
                      fontSize: "13px",
                      fontWeight: 800,
                      color: GOLD,
                      letterSpacing: "0.05em",
                      marginTop: "2px",
                      minWidth: "28px"
                    }}>
                          Q{String(i + 1).padStart(2, "0")}
                        </span>

                        <h3 style={{
                      fontFamily: FONT_HEAD,
                      fontWeight: 700,
                      fontSize: "15.5px",
                      lineHeight: 1.35,
                      color: "#ffffff",
                      letterSpacing: "-0.01em",
                      flex: 1
                    }} dangerouslySetInnerHTML={{
                      __html: faq.q
                    }} />
                      </div>

                      {/* Toggle icon */}
                      <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex items-center justify-center rounded-full flex-shrink-0 mt-0.5" style={{
                    width: 28,
                    height: 28,
                    backgroundColor: isOpen ? GOLD : "rgba(245,166,35,0.15)",
                    border: `1px solid ${isOpen ? GOLD : `${GOLD}44`}`,
                    transition: "all 0.2s ease"
                  }}>
                        {isOpen ? <Minus size={14} color={NAVY} strokeWidth={3} /> : <Plus size={14} color={GOLD} strokeWidth={3} />}
                      </span>
                    </button>

                    {/* Answer */}
                    {isOpen && <div className="px-5 md:px-6 pb-6" style={{
                  paddingLeft: "70px"
                }}>
                        <div className="pt-1 border-t" style={{
                    borderColor: "rgba(255,255,255,0.08)"
                  }}>
                          <p style={{
                      fontFamily: FONT_BODY,
                      fontSize: "14.5px",
                      lineHeight: 1.7,
                      color: "rgba(255,255,255,0.78)",
                      paddingTop: "16px"
                    }} dangerouslySetInnerHTML={{
                      __html: faq.a
                    }} />
                        </div>
                      </div>}
                  </li>;
            })}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom gold scanline */}
      <div aria-hidden className="absolute bottom-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent, ${GOLD}66 50%, transparent)`
    }} />
    </section>;
}